import React, { useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import logo from '../../../assets/logo.png';

const GlobalStyle = createGlobalStyle`
  body, html {
    background: transparent !important;
    margin: 0;
    padding: 0;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(16, 29, 64, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(16, 29, 64, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 29, 64, 0); }
`;

const dataFlow = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  10% { opacity: 0.8; }
  90% { opacity: 0.8; }
  100% { transform: translateY(200%); opacity: 0; }
`;

const gridPulse = keyframes`
  0% { opacity: 0.2; }
  50% { opacity: 0.5; }
  100% { opacity: 0.2; }
`;

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: -1;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    top: -50%;
    left: -50%;
    background: radial-gradient(circle, rgba(157, 109, 73, 0.05) 0%, rgba(16, 29, 64, 0.05) 100%);
    animation: rotate 30s linear infinite;
  }
  
  @keyframes rotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const DataGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(20, 1fr);
  grid-template-rows: repeat(20, 1fr);
  z-index: -2;
`;

const GridCell = styled.div`
  border: 1px solid rgba(16, 29, 64, 0.03);
  animation: ${gridPulse} ${props => props.duration}s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
`;

const DataStream = styled.div`
  position: absolute;
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  background: ${props => props.color};
  opacity: 0;
  left: ${props => props.left}%;
  top: 0;
  animation: ${dataFlow} ${props => props.duration}s linear infinite;
  animation-delay: ${props => props.delay}s;
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: ${props => props.color};
  border-radius: 50%;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  opacity: ${props => props.opacity};
`;

const LoginWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 450px;
  margin: 80px auto;
  padding: 32px 36px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1),
              0 1px 8px rgba(0, 0, 0, 0.05),
              0 0 0 1px rgba(16, 29, 64, 0.03);
  animation: ${fadeIn} 0.6s ease-out;
`;

const LoginContainer = styled.div`
  position: relative;
  z-index: 2;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const Logo = styled.img`
  height: 56px;
  margin-bottom: 6px;
`;

const Title = styled.h2`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  margin-bottom: 20px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  position: relative;
  animation: ${fadeIn} 0.4s ease-out;
  animation-delay: ${props => props.delay || 0}s;
  animation-fill-mode: both;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.3s;
  background: rgba(255, 255, 255, 0.85);

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.2);
  }

  &::placeholder {
    color: #aaa;
    opacity: 0.7;
  }
`;

const Button = styled.button`
  padding: 14px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s;
  margin-top: 4px;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  animation-delay: 0.3s;
  animation-fill-mode: both;

  &:hover {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(157, 109, 73, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  background-color: ${props => props.theme.colors.errorLight};
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 0.9rem;
  border-left: 3px solid ${props => props.theme.colors.error};
  animation: ${pulse} 2s infinite;
`;

const Branding = styled.div`
  text-align: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(16, 29, 64, 0.1);
  color: ${props => props.theme.colors.secondary};
  font-size: 0.85rem;
  font-weight: 500;
  opacity: 0.7;
  animation: ${fadeIn} 0.4s ease-out;
  animation-delay: 0.4s;
  animation-fill-mode: both;
`;

/**
 * AdminLogin - wraps the existing Login.js design for use in the Security Centre.
 * Accepts onSuccess callback to notify parent when authenticated.
 */
const AdminLogin = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [particles, setParticles] = useState([]);
  const [dataStreams, setDataStreams] = useState([]);
  const [gridCells, setGridCells] = useState([]);
  const navigate = useNavigate();

  // if already logged in, short-circuit
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && onSuccess) {
      onSuccess();
    }
  }, [onSuccess]);

  useEffect(() => {
    // Generate random particles for the background
    const newParticles = [];
    for (let i = 0; i < 25; i++) {
      newParticles.push({
        id: i,
        size: Math.random() * 12 + 5,
        top: Math.random() * 100,
        left: Math.random() * 100,
        opacity: Math.random() * 0.4 + 0.1,
        color: i % 2 === 0 ? 'rgba(16, 29, 64, 0.2)' : 'rgba(157, 109, 73, 0.2)',
      });
    }
    setParticles(newParticles);
    
    // Generate data streams
    const streams = [];
    for (let i = 0; i < 20; i++) {
      streams.push({
        id: i,
        width: Math.random() * 3 + 1,
        height: Math.random() * 250 + 100,
        left: Math.random() * 100,
        duration: Math.random() * 12 + 6,
        delay: Math.random() * 5,
        color: i % 3 === 0 
          ? `rgba(16, 29, 64, 0.5)` 
          : i % 3 === 1 
            ? `rgba(157, 109, 73, 0.5)` 
            : `rgba(100, 100, 100, 0.4)`
      });
    }
    setDataStreams(streams);
    
    // Generate grid cells
    const cells = [];
    for (let i = 0; i < 400; i++) {
      cells.push({
        id: i,
        duration: Math.random() * 4 + 2,
        delay: Math.random() * 2
      });
    }
    setGridCells(cells);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await fetch('https://api.belvoircastle.com:8008/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (onSuccess) onSuccess();
        // Optional: keep legacy redirect behavior if user navigates directly
        navigate('/security-center', { replace: true });
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    }
  };

  return (
    <>
      <GlobalStyle />
      <Background>
        <DataGrid>
          {gridCells.map(cell => (
            <GridCell 
              key={cell.id}
              duration={cell.duration}
              delay={cell.delay}
            />
          ))}
        </DataGrid>
        
        {dataStreams.map(stream => (
          <DataStream
            key={stream.id}
            width={stream.width}
            height={stream.height}
            left={stream.left}
            duration={stream.duration}
            delay={stream.delay}
            color={stream.color}
          />
        ))}
        
        {particles.map(particle => (
          <Particle 
            key={particle.id}
            size={particle.size}
            top={particle.top}
            left={particle.left}
            opacity={particle.opacity}
            color={particle.color}
          />
        ))}
      </Background>
      <LoginWrapper>
        <LoginContainer>
          <LogoContainer>
            <Logo src={logo} alt="Belvoir Castle Logo" />
          </LogoContainer>
          <Title>Security Centre Login</Title>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Form onSubmit={handleSubmit}>
            <InputGroup delay={0.05}>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>
            <InputGroup delay={0.1}>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </InputGroup>
            <Button type="submit">Sign In</Button>
          </Form>
          <Branding>Brought to you by Winters Web</Branding>
        </LoginContainer>
      </LoginWrapper>
    </>
  );
};

export default AdminLogin;

